import { IconInfo } from '@belong/icons';
import { ButtonBase, Text, Tooltip } from '@belong/ui';
import classNames from 'classnames/bind';
import GeneralIcon, { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import Label from 'components/Label/Label';
import ShadowMediaCard from 'components/ShadowMediaCard/ShadowMediaCard';
import { Text as LegacyText } from 'design-system';
import { MaintenanceRequester, SubOwnerVisibility } from 'models/enums';
import PropTypes from 'prop-types';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import {
  ResidentResponsibilityLabel,
  SharedResponsibilityLabel,
} from '../Labels/Labels';
import { SMART_LOCK_INSTALL_IMAGE_URL } from '../ImprovementModal/smart-lock-install-availability.utils';
import styles from './ImprovementCard.module.css';

const cx = classNames.bind(styles);

const PLACEHOLDER_IMAGE_URL =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/improvement-reports/proservice_placeholder_img.jpg';

/**
 * Annotated rather than rewritten, mirroring the `propTypes` block below. In a
 * `.js` file a parameter defaulted to `null` is inferred as type `null`, and one
 * with no default at all is inferred as a **required** prop — so passing an
 * `onClick` or a `customTopLeft` was an error, as was omitting the props the
 * card treats as optional.
 *
 * @param {{
 *   assessmentLabel?: boolean,
 *   borderRadius?: boolean,
 *   containerClassName?: string,
 *   customTopLeft?: ((improvement: any) => import('react').ReactNode) | null,
 *   height?: number,
 *   improvement: any,
 *   mediaContainerClassName?: string,
 *   onClick?: (() => void) | null,
 *   bottomRight?: import('react').ReactNode,
 *   isPreventative?: boolean,
 *   inspectionType?: any,
 *   isHomeownerView?: boolean,
 *   displayCleaningTooltip?: boolean,
 *   hideFlag?: boolean,
 * }} props
 */
const ImprovementCard = ({
  assessmentLabel = true,
  borderRadius = true,
  containerClassName = '',
  customTopLeft = null,
  height = 300,
  improvement,
  mediaContainerClassName = '',
  onClick = null,
  bottomRight = null,
  isPreventative: _isPreventative = false,
  inspectionType,
  isHomeownerView,
  displayCleaningTooltip,
  hideFlag = false,
}) => {
  const { maintenance, maintenancePayments } = improvement;
  const { isMoveOut: _isMoveOut } = inspectionType || {};
  const homeownerPayment = maintenancePayments.find(
    (payment) => payment.paidBy === MaintenanceRequester.Homeowner,
  );
  const residentPayment = maintenancePayments.find(
    (payment) => payment.paidBy === MaintenanceRequester.Resident,
  );
  const residentResponsiblity = residentPayment?.percentage === 100;
  const sharedResponsiblity =
    homeownerPayment?.percentage > 0 && residentPayment?.percentage > 0;

  const improvementMedia = maintenance.media?.filter(
    (mediaItem) =>
      ![SubOwnerVisibility.Consumer, SubOwnerVisibility.Internal].includes(
        mediaItem.subOwnerVisibility,
      ),
  );

  return (
    <ShadowMediaCard
      onClick={onClick}
      containerClassName={containerClassName}
      mediaContainerClassName={mediaContainerClassName}
      borderRadius={borderRadius}
      height={height}
      imageUrl={
        improvementMedia?.[0]?.url ||
        (maintenance.isAutomatedSmartLockInstallation
          ? SMART_LOCK_INSTALL_IMAGE_URL
          : PLACEHOLDER_IMAGE_URL)
      }
      bottomRight={bottomRight}
      topLeft={
        customTopLeft ? (
          customTopLeft(maintenance)
        ) : (
          <div className={cx('top-left-container')}>
            {assessmentLabel &&
              maintenance.requestedBy !== MaintenanceRequester.Belong && (
                <Label>
                  {POST_INSPECTION_FLOW_STRINGS['improvements.by-you']}
                </Label>
              )}

            <div className={cx('label-container flex flex-row items-center')}>
              <LegacyText className={cx('image-text')} fontWeight="semibold">
                {maintenance.summary}
              </LegacyText>
              {displayCleaningTooltip && <DeepCleaningTooltip />}
            </div>

            {sharedResponsiblity && (
              <div className={cx('label-container')}>
                <SharedResponsibilityLabel />
              </div>
            )}
            {residentResponsiblity && (
              <div className={cx('label-container')}>
                <ResidentResponsibilityLabel />
              </div>
            )}
          </div>
        )
      }
      bottomLeft={
        homeownerPayment?.flagged &&
        isHomeownerView &&
        !hideFlag && (
          <div className={cx('bottom-left-container')}>
            <GeneralIcon icon={GENERAL_ICONS.ICON_FLAG} />
            <span className={cx('flag-text')}>
              {POST_INSPECTION_FLOW_STRINGS['improvements.flag.text']}
            </span>
          </div>
        )
      }
    />
  );
};

ImprovementCard.propTypes = {
  assessmentLabel: PropTypes.bool,
  borderRadius: PropTypes.bool,
  containerClassName: PropTypes.string,
  customTopLeft: PropTypes.func,
  height: PropTypes.number,
  improvement: PropTypes.object.isRequired,
  mediaContainerClassName: PropTypes.string,
  onClick: PropTypes.func,
  bottomRight: PropTypes.node,
  isPreventative: PropTypes.bool,
  inspectionType: PropTypes.string,
  isHomeownerView: PropTypes.bool,
  hideFlag: PropTypes.bool,
};

export default ImprovementCard;

const DeepCleaningTooltip = () => {
  return (
    <Tooltip
      contentProps={{ side: 'top' }}
      arrowProps={{ className: 'fill-bg-black' }}
      content={
        <div className="rounded bg-black p-xs" style={{ width: 300 }}>
          <Text className="p1 whitespace-pre-line text-left font-regular text-white">
            With different definitions of clean, Belong standardizes deep
            cleaning for every home to maintain a consistent level of quality.
          </Text>
        </div>
      }
    >
      <ButtonBase
        className="z-40 ml-[8px] flex cursor-pointer items-center fill-white text-white"
        type="button"
        onClick={(e) => e.stopPropagation()}
      >
        <IconInfo width={18} />
      </ButtonBase>
    </Tooltip>
  );
};
